import { Component, OnInit, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { ApiService } from '../../services/api/api.service';
import { FormatService } from '../../services/format/format.service';
import * as moment from 'moment';
import { targetLabel, turnTag, scopeLabel, recordLabel, currentTurnLabel } from '../../services/tilawah-period';

// Read-only monitoring of tilawah (group reading) — no create/edit/delete
// anywhere on this page: admins observe, group admins act in the app.
@Component({
  selector: 'app-tilawah',
  templateUrl: './tilawah.component.html',
  styleUrls: ['./tilawah.component.css']
})
export class TilawahComponent implements OnInit {
  isView : string = 'groups'; // 'groups' | 'activity'

  // KPI strip
  kpi : any = null;

  // ===== groups table =====
  isData : any = [];
  isTotalAll : number = 0;
  isKeyword : string = '';
  isStatus : string = '';
  isSort : string = 'newest';
  isPage : number = 1;
  isPageSize : number = 10;
  isPages : any = [];
  isTableLoading : boolean = false;

  // ===== activity feed =====
  actData : any = [];
  actTotal : number = 0;
  actKind : string = '';
  actDays : string = '30';
  actKeyword : string = '';
  actPage : number = 1;
  actPages : any = [];
  actLoading : boolean = false;

  directoryFile : string = '';

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private authService: AuthenticationService,
    private apiService: ApiService,
    private formatService: FormatService,
  ) { }

  ngOnInit(): void {
    moment.locale('id');
    this.isPageSize = this.apiService.LIMIT || 10;
    this.loadSummary();
    this.getData();
  }

  setView(view : string){
    if(this.isView === view){ return; }
    this.isView = view;
    if(view === 'activity' && !this.actData.length && !this.actLoading){ this.getActivity(); }
  }

  // ===== KPI =====
  loadSummary(){
    this.apiService.methodGetSilent(`tilawah/summary`, this.authService.getToken()).subscribe((response) => {
      if(response && response.status == 200){ this.kpi = response.data; }
    });
  }
  todayPct(){
    if(!this.kpi || !this.kpi.today_total){ return null; }
    return Math.round(this.kpi.today_done / this.kpi.today_total * 100);
  }

  // ===== groups: search / filter / pager =====
  private searchTimer : any;
  private lastQuery : string = '';

  onKeywordInput(value : string){
    this.isKeyword = value;
    clearTimeout(this.searchTimer);
    // debounce: wait 450ms after the last keystroke before hitting the API
    this.searchTimer = setTimeout(() => this.applyFilter(), 450);
  }

  async applyFilter(){
    clearTimeout(this.searchTimer);
    const query = `${this.isKeyword}|${this.isStatus}|${this.isSort}`;
    if(query === this.lastQuery){ return; } // query unchanged, skip the request
    this.lastQuery = query;
    this.isPage = 1;
    await this.getData();
  }

  async getData(){
    const searchData = {"keyword": this.isKeyword, "status": this.isStatus, "sort": this.isSort};
    const offset = (this.isPage - 1) * this.isPageSize;
    this.isTableLoading = true;
    try{
      this.apiService.methodGetSilent(`tilawah/${offset}/${encodeURIComponent(JSON.stringify(searchData))}`, this.authService.getToken()).subscribe((response) => {
        this.isTableLoading = false;
        if(response && response.status == 200){
          this.directoryFile = response.directoryFile || '';
          this.isData = (response.data || []).map((v : any) => this.decorate(v));
          this.isTotalAll = response.total || 0;
          this.buildPager();
        }
      }, () => { this.isTableLoading = false; });
    }catch(error){
      this.isTableLoading = false;
      console.log(error);
    }
  }

  // view-model fields; relations (owner) can be null when the account is gone
  private decorate(v : any){
    v.photoUrl = this.photoUrl(v.photo, 'tilawah/');
    v.ownerPhotoUrl = this.photoUrl(v.owner_photo, 'user/avatar/');
    v.ownerName = v.owner_name || '—';
    v.targetVw = targetLabel(v, (n) => this.formatNumber(n));
    v.scopeVw = scopeLabel(v.reading_scope);
    v.recordVw = recordLabel(v.reading_record);
    v.turnVw = turnTag(v.reading_period, this.formatNumber(v.current_day), false);
    v.currentTurnVw = currentTurnLabel(v.reading_period);
    v.lastActivityVw = v.last_activity_at ? moment(v.last_activity_at).fromNow() : 'Belum ada';
    v.lastActivityFull = v.last_activity_at ? moment(v.last_activity_at).format('D MMMM YYYY HH:mm') : '';
    v.createdVw = v.created_at ? moment(v.created_at).format('D MMM YYYY') : '';
    v.todayPct = v.today_total ? Math.round(v.today_done / v.today_total * 100) : null;
    return v;
  }

  photoUrl(photo : any, folder : string){
    if(!photo){ return ''; }
    return String(photo).includes('http') ? photo : `${this.directoryFile}${folder}${photo}`;
  }

  statusLabel(s : string){
    if(s === 'active'){ return 'Aktif'; }
    if(s === 'deleted'){ return 'Dihapus'; }
    return 'Senyap';
  }

  openDetail(v : any){
    this.router.navigate(['/tilawah', v.id]);
  }

  totalPages(){
    return Math.max(1, Math.ceil(this.isTotalAll / this.isPageSize));
  }
  buildPager(){
    this.isPages = this.pagesFor(this.isPage, this.totalPages());
  }
  private pagesFor(current : number, total : number){
    const pages: any[] = [];
    for(let p = 1; p <= total; p++){
      if(p == 1 || p == total || Math.abs(p - current) <= 1){
        pages.push(p);
      }else if(pages[pages.length - 1] != '...'){
        pages.push('...');
      }
    }
    return pages;
  }
  async goToPage(p: any){
    const total = this.totalPages();
    if(p < 1 || p > total || p == this.isPage){ return; }
    this.isPage = p;
    await this.getData();
  }
  rowNo(i: number){
    return (this.isPage - 1) * this.isPageSize + i + 1;
  }
  summaryFrom(){
    return this.isTotalAll ? (this.isPage - 1) * this.isPageSize + 1 : 0;
  }
  summaryTo(){
    return (this.isPage - 1) * this.isPageSize + this.isData.length;
  }
  formatNumber(value : any){
    return this.formatService.dotFormat(value || 0);
  }

  // wrap keyword matches in <mark> for the list; input is HTML-escaped first
  highlight(text : any, keyword : string = this.isKeyword){
    const esc = String(text == null ? '' : text)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const kw = (keyword || '').trim();
    if(!kw){ return esc; }
    const escapedKeyword = kw
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // neutralize regex metacharacters
    return esc.replace(new RegExp(`(${escapedKeyword})`, 'gi'), '<mark class="vl-hl">$1</mark>');
  }

  // ===== global activity feed =====
  private actTimer : any;
  private actLastQuery : string = '';

  onActKeywordInput(value : string){
    this.actKeyword = value;
    clearTimeout(this.actTimer);
    this.actTimer = setTimeout(() => this.applyActFilter(), 450);
  }
  async applyActFilter(){
    clearTimeout(this.actTimer);
    const query = `${this.actKeyword}|${this.actKind}|${this.actDays}`;
    if(query === this.actLastQuery){ return; }
    this.actLastQuery = query;
    this.actPage = 1;
    await this.getActivity();
  }
  async getActivity(){
    const searchData = {"keyword": this.actKeyword, "kind": this.actKind, "days": this.actDays};
    const offset = (this.actPage - 1) * this.isPageSize;
    this.actLoading = true;
    try{
      this.apiService.methodGetSilent(`tilawah/activity/${offset}/${encodeURIComponent(JSON.stringify(searchData))}`, this.authService.getToken()).subscribe((response) => {
        this.actLoading = false;
        if(response && response.status == 200){
          this.directoryFile = response.directoryFile || this.directoryFile;
          this.actData = (response.data || []).map((a : any) => this.decorateActivity(a));
          this.actTotal = response.total || 0;
          this.actPages = this.pagesFor(this.actPage, this.actTotalPages());
        }
      }, () => { this.actLoading = false; });
    }catch(error){
      this.actLoading = false;
      console.log(error);
    }
  }
  actTotalPages(){
    return Math.max(1, Math.ceil(this.actTotal / this.isPageSize));
  }
  async goToActPage(p : any){
    if(p < 1 || p > this.actTotalPages() || p == this.actPage){ return; }
    this.actPage = p;
    await this.getActivity();
  }
  actFrom(){ return this.actTotal ? (this.actPage - 1) * this.isPageSize + 1 : 0; }
  actTo(){ return (this.actPage - 1) * this.isPageSize + this.actData.length; }

  // shared with the detail page — keep both in sync
  static readonly KIND_META : any = {
    member_joined:   {label: 'Bergabung',        icon: 'fa-user-plus',   tone: 'ok'},
    member_removed:  {label: 'Dikeluarkan',      icon: 'fa-user-minus',  tone: 'warn'},
    group_deleted:   {label: 'Grup dihapus',     icon: 'fa-trash',       tone: 'danger'},
    khatam_reset:    {label: 'Khatam baru',      icon: 'fa-sync',        tone: 'primary'},
    task_created:    {label: 'Tugas bacaan',     icon: 'fa-book-open',   tone: 'primary'},
    task_done:       {label: 'Selesai baca',     icon: 'fa-check',       tone: 'ok'},
    juz_complete:    {label: 'Juz tuntas',       icon: 'fa-star',        tone: 'gold'},
    khatam:          {label: 'Khatam',           icon: 'fa-flag-checkered', tone: 'gold'},
    task_reassigned: {label: 'Limpahan bagian',  icon: 'fa-exchange-alt', tone: 'warn'},
    announcement:    {label: 'Pengumuman',       icon: 'fa-bullhorn',    tone: 'primary'},
    system:          {label: 'Sistem',           icon: 'fa-info',        tone: 'muted'},
    message:         {label: 'Pesan',            icon: 'fa-comment',     tone: 'muted'},
  };
  kindMeta(kind : string){
    return TilawahComponent.KIND_META[kind] || TilawahComponent.KIND_META.system;
  }
  kinds(){
    return Object.keys(TilawahComponent.KIND_META).filter(k => k !== 'message' && k !== 'system');
  }

  private decorateActivity(a : any){
    a.meta = this.kindMeta(a.kind);
    a.timeVw = a.created_at ? moment(a.created_at).fromNow() : '';
    a.timeFull = a.created_at ? moment(a.created_at).format('D MMMM YYYY HH:mm') : '';
    a.groupPhotoUrl = this.photoUrl(a.tilawah_photo, 'tilawah/');
    a.textVw = this.activityText(a);
    return a;
  }

  // one readable sentence per row; falls back to the stored chat text
  activityText(a : any){
    const who = a.user_name || 'Seseorang';
    switch(a.kind){
      case 'task_created':
        return a.task_day ? `${who} membuat tugas bacaan ${turnTag(a.reading_period, a.task_day)}` : `${who} membuat tugas bacaan`;
      case 'announcement':
        return `Pengumuman: ${a.announcement_title || a.text || ''}`;
      case 'message':
        return `${who}: ${a.text || (a.has_media ? '(media)' : '')}`;
      default:
        return a.text || '';
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(){ /* no dialogs on this page — kept for parity with other pages */ }
}
